import { supabase } from "@/integrations/supabase/client";
import { ApiService } from "@/services/api/ApiService";
import { ActivationFunnelService } from "@/services/analytics/ActivationFunnelService";
import { SITE_URL } from "@/config/site";
import {
  ActivationPack,
  ActivationPackWithTarget,
  CreateActivationPackParams,
  PromoTemplate,
  PromoTemplateType,
  PromoTone,
  PublicActivationDTO,
  UpdateActivationPackParams,
  DEFAULT_CHANNEL_CONFIG,
  DEFAULT_TOUCHPOINT_CONFIG,
  DEFAULT_ASSET_CONFIG,
} from "@/types/activation/activation-pack";
import type { GenieBranding } from "@/types/ui/branding";

export interface ActivationEmailDeliveryResult {
  email: string;
  sent: boolean;
  state: "not_sent" | "sent" | "unknown";
  error?: string;
}

export class ActivationPackService {
  // =====================
  // Internal helpers
  // =====================

  /**
   * The API embeds the agent's page/phone as nested resources, which Supabase
   * returns as arrays. Collapse them to single objects to match the
   * ActivationPackWithTarget shape consumers expect.
   */
  private static normalizePackTarget<T extends ActivationPackWithTarget>(
    pack: T,
  ): T {
    const agent = pack.agent as
      | (NonNullable<ActivationPackWithTarget["agent"]> & {
          page?: unknown;
          phone?: unknown;
        })
      | null
      | undefined;
    if (agent) {
      const page = agent.page as unknown;
      const phone = agent.phone as unknown;
      agent.page = (
        Array.isArray(page) ? (page[0] ?? null) : (page ?? null)
      ) as typeof agent.page;
      agent.phone = (
        Array.isArray(phone) ? (phone[0] ?? null) : (phone ?? null)
      ) as typeof agent.phone;
    }
    return pack;
  }

  // =====================
  // CRUD Operations
  // =====================

  static async createPack(
    params: CreateActivationPackParams,
  ): Promise<ActivationPack | null> {
    try {
      // Validate that exactly one target is specified
      if (
        (!params.agent_id && !params.portal_id) ||
        (params.agent_id && params.portal_id)
      ) {
        throw new Error(
          "Exactly one of agent_id or portal_id must be specified",
        );
      }

      // Activation packs become permanent physical and digital collateral.
      // Always bake the canonical public origin into generated links so a pack
      // prepared from localhost or a staging preview can still be printed and
      // handed to a customer safely.
      const baseUrl = SITE_URL;

      // Route through the API so the handler can kick off background promo
      // generation (server-side AI calls) after insert.
      const response = await ApiService.invoke<{
        activationPack: ActivationPack;
      }>({
        resource: "activation-packs",
        action: "create",
        data: {
          name: params.name,
          agent_id: params.agent_id || null,
          portal_id: params.portal_id || null,
          description: params.description || null,
          channels: { ...DEFAULT_CHANNEL_CONFIG, ...params.channels },
          touchpoints: { ...DEFAULT_TOUCHPOINT_CONFIG, ...params.touchpoints },
          branding_overrides: params.branding_overrides || null,
          asset_config: { ...DEFAULT_ASSET_CONFIG, ...params.asset_config },
          ...(baseUrl ? { baseUrl } : {}),
        },
      });

      const created = response?.activationPack ?? null;

      // Activation funnel — a pack now exists. Emitted here, not at the call
      // sites, because this is the single point every path goes through:
      // `/welcome`, GenieSetup, persona quick-create, Genie clone (all via
      // `createDefaultPackForGenie`), the by-Genie redirect, and manual pack
      // creation. `createDefaultPackForGenie` returns early when a pack is
      // already there, so this cannot fire twice for the same Genie.
      if (created) {
        ActivationFunnelService.activationPackGenerated(params.agent_id ?? null);
      }

      return created;
    } catch (error) {
      console.error("Error creating activation pack:", error);
      throw error;
    }
  }

  /**
   * Idempotently create a default activation pack for a Genie.
   * No-op (returns existing) if the agent already has a pack.
   * Used at agent-creation time to ensure every new Genie has a pack waiting.
   */
  static async createDefaultPackForGenie(
    agentId: string,
    agentName?: string,
  ): Promise<ActivationPack | null> {
    try {
      const existing = await this.listPacksForAgent(agentId);
      if (existing.length > 0) {
        return existing[0];
      }

      let resolvedName = agentName;
      if (!resolvedName) {
        const { data: agent } = await supabase
          .from("agents")
          .select("name")
          .eq("id", agentId)
          .single();
        resolvedName = agent?.name ?? "My Genie";
      }

      return await this.createPack({
        agent_id: agentId,
        name: `${resolvedName} Activation Pack`,
        description:
          "Your ready-to-ship toolkit for getting your Genie in front of customers — QR codes, embed snippet, email signature, and print-ready assets.",
        // SMS and WhatsApp stay off: nothing answers a text or WhatsApp
        // message on a genie number yet, so a pack must not offer either.
        channels: {
          web_widget: true,
          phone: true,
          sms: false,
          whatsapp: false,
        },
        touchpoints: {
          qr_sticker: true,
          email_signature: true,
          social_card: true,
        },
      });
    } catch (error) {
      console.error("Error creating default activation pack for genie:", error);
      return null;
    }
  }

  static async getPack(packId: string): Promise<ActivationPack | null> {
    try {
      const { data, error } = await supabase
        .from("activation_packs")
        .select("*")
        .eq("id", packId)
        .single();

      if (error) return null;
      return data as ActivationPack;
    } catch (error) {
      console.error("Error fetching activation pack:", error);
      return null;
    }
  }

  static async getPackWithTarget(
    packId: string,
  ): Promise<ActivationPackWithTarget | null> {
    try {
      // Route through the API so admin impersonation is honored — a direct
      // client query is RLS-scoped to the real session and can't see the
      // impersonated customer's packs.
      const response = await ApiService.invoke<{
        activationPack: ActivationPackWithTarget;
      }>({
        resource: "activation-packs",
        action: "get",
        id: packId,
      });
      const pack = response?.activationPack;
      return pack ? this.normalizePackTarget(pack) : null;
    } catch (error) {
      const apiError = (error as { apiError?: { code?: string } })?.apiError;
      if (apiError?.code === "NOT_FOUND") return null;
      console.error("Error fetching activation pack with target:", error);
      return null;
    }
  }

  static async listPacks(
    _userId?: string,
  ): Promise<ActivationPackWithTarget[]> {
    try {
      // The API scopes to the caller's own packs (or the impersonated customer's
      // when an admin is impersonating — ApiService attaches that automatically),
      // so no userId is passed here.
      const response = await ApiService.invoke<{
        items: ActivationPackWithTarget[];
      }>({
        resource: "activation-packs",
        action: "list",
        data: { limit: 500 },
      });
      return (response?.items || []).map((pack) =>
        this.normalizePackTarget(pack),
      );
    } catch (error) {
      console.error("Error listing activation packs:", error);
      return [];
    }
  }

  static async listPacksForAgent(agentId: string): Promise<ActivationPack[]> {
    try {
      const response = await ApiService.invoke<{ items: ActivationPack[] }>({
        resource: "activation-packs",
        action: "list",
        data: { agent_id: agentId, limit: 500 },
      });
      return response?.items || [];
    } catch (error) {
      console.error("Error listing packs for agent:", error);
      return [];
    }
  }

  static async listPacksForPortal(portalId: string): Promise<ActivationPack[]> {
    try {
      const response = await ApiService.invoke<{ items: ActivationPack[] }>({
        resource: "activation-packs",
        action: "list",
        data: { portal_id: portalId, limit: 500 },
      });
      return response?.items || [];
    } catch (error) {
      console.error("Error listing packs for portal:", error);
      return [];
    }
  }

  static async updatePack(
    packId: string,
    updates: UpdateActivationPackParams,
  ): Promise<ActivationPack | null> {
    try {
      const response = await ApiService.invoke<{
        activationPack: ActivationPack;
      }>({
        resource: "activation-packs",
        action: "update",
        id: packId,
        data: { ...updates },
      });
      return response?.activationPack ?? null;
    } catch (error) {
      console.error("Error updating activation pack:", error);
      throw error;
    }
  }

  static async deletePack(packId: string): Promise<boolean> {
    try {
      await ApiService.invoke<{ success: boolean }>({
        resource: "activation-packs",
        action: "delete",
        id: packId,
      });
      return true;
    } catch (error) {
      console.error("Error deleting activation pack:", error);
      return false;
    }
  }

  // =====================
  // URL Generation Utilities
  // =====================

  static getTargetUrl(pack: ActivationPackWithTarget): string {
    const baseUrl = SITE_URL;

    const agentUrlName = pack.agent?.url_name ?? pack.agent?.page?.url_name;
    if (agentUrlName) {
      return `${baseUrl}/live-agent/${encodeURIComponent(agentUrlName)}`;
    }

    if (pack.portal) {
      return `${baseUrl}/portal/${encodeURIComponent(pack.portal.slug)}`;
    }

    return baseUrl;
  }

  static getPhoneUrl(pack: ActivationPackWithTarget): string | null {
    if (pack.agent?.phone?.phone_number) {
      return `tel:${pack.agent.phone.phone_number}`;
    }
    return null;
  }

  static getSmsUrl(pack: ActivationPackWithTarget): string | null {
    if (pack.agent?.phone?.phone_number) {
      return `sms:${pack.agent.phone.phone_number}`;
    }
    return null;
  }

  /**
   * The device-detecting "smart link" interstitial (`/get/:packId`). Scanning
   * the activation QR or tapping an app-onboarding CTA lands here, then routes
   * to the App Store / Play Store / web Genie based on device. Single source of
   * truth — every QR/poster/section/zip uses this, so the slug-vs-id decision
   * changes in exactly one place.
   */
  static getAppSmartLinkUrl(
    pack: ActivationPackWithTarget,
    source = "activation-pack",
  ): string {
    const params = new URLSearchParams({ intent: "install", source });
    return `${SITE_URL}/get/${pack.id}?${params.toString()}`;
  }

  /**
   * The immersive, branded activation microsite for this pack
   * (`/activate/:packId`) — a public, shareable page that shows a customer how
   * to activate this Genie.
   */
  static getPresentationUrl(pack: ActivationPackWithTarget): string {
    return `${SITE_URL}/activate/${pack.id}`;
  }

  /**
   * Emails the pack's activation presentation or app smart link to a list of
   * recipients (typically default-filled from the caller's team).
   */
  static async sendPackLink(
    packId: string,
    emails: string[],
    kind: "presentation" | "app",
    campaignId: string,
  ): Promise<ActivationEmailDeliveryResult[]> {
    const response = await ApiService.invoke<{
      results: ActivationEmailDeliveryResult[];
    }>({
      resource: "activation-packs",
      action: "send-link",
      id: packId,
      data: { emails, kind, campaignId },
    });
    return response?.results ?? [];
  }

  /**
   * The one-click "Activate your customers" action — sends ONE combined email
   * per recipient covering the presentation, the app link, and (for
   * agent-backed packs) a working Genie access link. No dashboard access is
   * granted — these are customers, not people logging into HelpGenie.
   */
  static async activateCustomers(
    packId: string,
    emails: string[],
    campaignId: string,
  ): Promise<ActivationEmailDeliveryResult[]> {
    const response = await ApiService.invoke<{
      results: ActivationEmailDeliveryResult[];
    }>({
      resource: "activation-packs",
      action: "activate-team",
      id: packId,
      data: { emails, campaignId },
    });
    return response?.results ?? [];
  }

  static getEffectiveBranding(
    pack: ActivationPackWithTarget,
  ): Partial<GenieBranding> {
    // Start with target's branding
    const baseBranding = pack.agent?.branding || pack.portal?.branding || {};

    // Override with pack-specific customizations
    if (pack.branding_overrides) {
      return { ...baseBranding, ...pack.branding_overrides };
    }

    return baseBranding;
  }

  static getTargetName(pack: ActivationPackWithTarget): string {
    return pack.agent?.name || pack.portal?.name || "Unknown";
  }

  static getTargetType(pack: ActivationPackWithTarget): "genie" | "portal" {
    return pack.agent_id ? "genie" : "portal";
  }

  // =====================
  // Promo Content
  // =====================

  static async generatePromo(
    packId: string,
    template: PromoTemplateType,
    tone: PromoTone,
    targetUrl: string,
  ): Promise<{
    template: PromoTemplateType;
    tone: PromoTone;
    content: PromoTemplate;
  } | null> {
    try {
      const response = await ApiService.invoke<{
        template: PromoTemplateType;
        tone: PromoTone;
        content: PromoTemplate;
      }>({
        resource: "activation-packs",
        action: "generate-promo",
        id: packId,
        data: { template, tone, targetUrl },
      });

      return response || null;
    } catch (error) {
      console.error("Error generating promo content:", error);
      throw error;
    }
  }

  // =====================
  // Public (unauthenticated) read
  // =====================

  /**
   * Fetch the sanitized public view of a pack for the activation microsite /
   * smart link. Returns null when the pack or target no longer exists. Private
   * Genie conversations remain protected by their recipient access token.
   */
  static async getPublicPack(
    packId: string,
  ): Promise<PublicActivationDTO | null> {
    try {
      const response = await ApiService.invoke<{
        activation: PublicActivationDTO;
      }>(
        { resource: "activation", action: "public-get", id: packId },
        "public",
      );
      return response?.activation || null;
    } catch (error) {
      const apiError = (error as { apiError?: { code?: string } })?.apiError;
      if (apiError?.code === "NOT_FOUND") return null;
      throw error;
    }
  }
}
